import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext(null);

export const ThemeProvider = ({ children }) => {
  // Read stored preference or default to 'system'
  const [themeMode, setThemeMode] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('themeMode') || 'system';
    }
    return 'system';
  });

  const [resolvedTheme, setResolvedTheme] = useState('dark');

  useEffect(() => {
    const applyTheme = () => {
      let effectiveTheme = themeMode;

      if (themeMode === 'system') {
        // Detect system preference (works on iOS, Android, Web)
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        effectiveTheme = mediaQuery.matches ? 'dark' : 'light';
      }

      setResolvedTheme(effectiveTheme);

      // Apply to document
      const root = document.documentElement;
      if (effectiveTheme === 'dark') {
        root.classList.add('dark');
        root.classList.remove('light');
        root.style.setProperty('--bg-primary', '#0B0C10');
        root.style.setProperty('--text-primary', '#F9FAFB');
        root.style.setProperty('--text-muted', '#9CA3AF');
        root.style.setProperty('--surface', 'rgba(255, 255, 255, 0.03)');
        root.style.setProperty('--surface-hover', 'rgba(255, 255, 255, 0.06)');
        root.style.setProperty('--border', 'rgba(255, 255, 255, 0.08)');
      } else {
        root.classList.add('light');
        root.classList.remove('dark');
        root.style.setProperty('--bg-primary', '#FAFAFA');
        root.style.setProperty('--text-primary', '#0B0C10');
        root.style.setProperty('--text-muted', '#6B7280');
        root.style.setProperty('--surface', 'rgba(0, 0, 0, 0.03)');
        root.style.setProperty('--surface-hover', 'rgba(0, 0, 0, 0.06)');
        root.style.setProperty('--border', 'rgba(0, 0, 0, 0.08)');
      }
    };

    applyTheme();

    // Listen for system theme changes when in 'system' mode
    if (themeMode === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handler = () => applyTheme();
      mediaQuery.addEventListener('change', handler);
      return () => mediaQuery.removeEventListener('change', handler);
    }
  }, [themeMode]);

  const changeTheme = (newMode) => {
    setThemeMode(newMode);
    localStorage.setItem('themeMode', newMode);
  };

  return (
    <ThemeContext.Provider value={{ themeMode, resolvedTheme, changeTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme debe ser usado dentro de ThemeProvider');
  }
  return context;
};
